import { JobIDType } from '@/types';
import { PayoutFrequency } from '@/graphql/generated/graphql';
import startCase from 'lodash/startCase';

export function arrayUnique<T, V>(arr: Array<T>, valueHandler: (item: T) => V): Array<V> {
  if (!Array.isArray(arr)) {
    throw new Error('arrayUnique function works only with arrays!');
  }

  const values = arr.map(valueHandler);
  return Array.from(new Set(values));
}

export interface IFormatNumToLocate {
  number?: number | null;
  style?: 'currency';
  locale?: string | null;
  currency?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  currencyMinimumFractionDigits?: number;
  currencyMaximumFractionDigits?: number;
  showFallbackValue?: boolean;
  showPlusSign?: boolean;
  signDisplay?: 'auto' | 'always' | 'exceptZero' | 'never';
}

export function formatNumberToLocale({
  number,
  style,
  maximumFractionDigits = 0,
  minimumFractionDigits = 0,
  currencyMaximumFractionDigits,
  currencyMinimumFractionDigits,
  locale = 'en-US',
  currency = 'USD',
  showFallbackValue = false,
  showPlusSign = false,
}: IFormatNumToLocate): string {
  if (number === null || number === undefined) {
    if (showFallbackValue) {
      if (style === 'currency') {
        const numberFormat = new Intl.NumberFormat(locale || 'en-US', {
          currency: 'USD',
        });
        return numberFormat.format(0);
      } else {
        return '0';
      }
    } else {
      return '';
    }
  }
  const options: IFormatNumToLocate = { style };

  if (style === 'currency') {
    options.currency = currency;
    const fractionLength = String(number).split('.')[1]?.length || 0;
    options.maximumFractionDigits = currencyMaximumFractionDigits ?? fractionLength;
    options.minimumFractionDigits = currencyMinimumFractionDigits ?? fractionLength;
  } else {
    options.maximumFractionDigits = maximumFractionDigits;
    options.minimumFractionDigits = minimumFractionDigits;
    options.signDisplay = showPlusSign ? 'always' : 'never';
  }

  try {
    const numberFormat = new Intl.NumberFormat(locale || 'en-US', options);
    return numberFormat.format(number);
  } catch {
    const numberFormat = new Intl.NumberFormat(locale || 'en-US');
    if (currency) {
      if (currency === 'multiple' && number > 0) {
        return 'MULTI'.concat(showPlusSign ? ' +' : ' ', numberFormat.format(number));
      }
      let abbreviation = currency;

      if (startCase(currency).split(' ').length > 1) {
        abbreviation = startCase(currency)
          .split(' ')
          .map((word) => word.charAt(0))
          .join('');
      }

      return abbreviation.concat(showPlusSign && number > 0 ? ' +' : ' ', numberFormat.format(number));
    } else {
      return numberFormat.format(number);
    }
  }
}

export const capitalize = (value?: string | null): string => {
  if (typeof value !== 'string') return '';

  return value
    .split(/[\s_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1))
    .join(' ');
};

export function queryUrlString(name: string, url = window.location.href) {
  const changedName = name.replace(/[[]]/g, '\\$&');

  const regex = new RegExp('[?&]' + changedName + '(=([^&#]*)|&|#|$)', 'i');
  const results = regex.exec(url);

  if (!results) {
    return null;
  }
  if (!results[2]) {
    return '';
  }

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function parseCurrencyAmount(value: string): number {
  const example = Intl.NumberFormat('en-US').format(1.1);
  const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, 'g');
  const cleaned = value.replace(cleanPattern, '');
  const normalized = cleaned.replace(example.charAt(1), '.');

  return parseFloat(normalized);
}

export const chunk = (arr: Array<any>, size: number) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));

export function formatTaskName({
  jobId,
  payoutFrequency,
  planComponentName,
}: {
  jobId?: string | null;
  payoutFrequency?: PayoutFrequency | null;
  planComponentName?: string | null;
}): string {
  let taskName = jobId;

  const regexp = new RegExp(`${JobIDType.Manual}`, 'g');
  if (payoutFrequency !== PayoutFrequency.OnEvent) {
    if (payoutFrequency === PayoutFrequency.ManualPayout) {
      taskName = jobId?.replace(regexp, '');
    } else {
      if (planComponentName) {
        taskName = planComponentName;
      }
    }
  } else {
    if (taskName?.startsWith(JobIDType.Manual)) {
      taskName = jobId?.replace(regexp, '');
    }
  }

  if (taskName?.startsWith('__arya_external_')) {
    taskName = jobId?.replace('__arya_external_', '');
  }

  return taskName || jobId || 'N/A';
}

export function removeJobIdPrefix(jobId: string) {
  let taskName = jobId;

  if (taskName.startsWith('__arya_external_')) {
    taskName = jobId.replace('__arya_external_', '');
  }

  if (taskName.startsWith('__scheduled_day_')) {
    taskName = jobId.replace('__scheduled_day_', '');
  }

  if (taskName.startsWith('__scheduled_custom_')) {
    taskName = jobId.replace('__scheduled_custom_', '');
  }

  if (taskName.startsWith('__manual_')) {
    taskName = jobId.replace('__manual_', '');
  }

  return taskName;
}

export const isISOString = (dateString?: string) => {
  if (dateString) {
    const date = new Date(dateString);
    return !Number.isNaN(date.valueOf()) && date.toISOString() === dateString;
  }
  return false;
};

export function convertMinutesToHoursAndMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return { hours, minutes: remainingMinutes };
}

export default function formatNumberWithThreshold(value?: number) {
  const map = [
    { suffix: 't', threshold: 1e12 },
    { suffix: 'b', threshold: 1e9 },
    { suffix: 'm', threshold: 1e6 },
    { suffix: 'k', threshold: 1e3 },
    { suffix: '', threshold: 1 },
  ];

  if (value) {
    const absValue = Math.abs(value);
    const found = map.find((x) => absValue >= x.threshold);

    if (found) {
      const formattedValue = value / found.threshold;
      return formattedValue % 1 !== 0 ? formattedValue.toFixed(0) + found.suffix : formattedValue + found.suffix;
    }
  }

  return '0';
}

export function generateRandomHexColors(numColors: number): string[] {
  const colors = [];

  for (let i = 0; i < numColors; i++) {
    // Generate RGB values with better contrast by ensuring variation
    // Each component ranges from 0-255, but we ensure at least one is dark (<128) and one is bright (>127)
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);

    // Calculate if color is too pale (all components are high)
    const isPale = r > 200 && g > 200 && b > 200;
    // Calculate if color is too dark (all components are low)
    const isTooDark = r < 55 && g < 55 && b < 55;

    // Regenerate if color has poor contrast
    if (isPale || isTooDark) {
      i--;
      continue;
    }

    const color =
      '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
    colors.push(color.toUpperCase());
  }

  return colors;
}

export const displayName = (firstName?: string | null, lastName?: string | null, placeholder?: string | null) => {
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim() || placeholder;
  return capitalize(name);
};

// Utility to check if a URL is a PDF
export function isPdfUrl(url?: string): boolean {
  if (!url) return false;
  return url.toLowerCase().includes('.pdf') || url.toLowerCase().startsWith('data:application/pdf');
}
